import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { UsersService } from '@app/services/users.service';
import { MatExpansionPanel } from '@angular/material/expansion';
import { OfferStepsDesktop, OfferStepsEnum, OfferStepsMobile } from '../enums/offer-steps.enum';
import { Router } from '@angular/router';
import { ApiService } from '@app/services/api.service';
import { DeviceService } from '@app/services/device.service';
import { DialogService } from '@app/modules/dialogs/dialog.service';
import { BridgeService } from '@app/services/bridge.service';
import { MixpanelService } from '@app/services/mixpanel.service';
import { FillingScoreService } from '@app/services/filling-score.service';
import { Id, Params } from '@feathersjs/feathers';
import { MortgageInterface } from '@app/interfaces/mortgage.interface';
import { DealsAutoService } from '@app/services/deals-auto.service';

@Injectable({ providedIn: 'root' })
export class MortgagesService extends ApiService<MortgageInterface> {
    private readonly usersService = inject(UsersService);
    private readonly dealsAutoService = inject(DealsAutoService);
    private readonly dialogService = inject(DialogService);
    private readonly router = inject(Router);
    private readonly deviceService = inject(DeviceService);
    private readonly bridgeService = inject(BridgeService);
    private readonly mixpanelService = inject(MixpanelService);

    public benefitToggle: Subject<boolean> = new Subject();
    public benefitToggleObservable: Observable<boolean> = this.benefitToggle.asObservable();
    isDesktop: boolean;
    public mortgage: MortgageInterface = null;
    public preventNavigate: boolean = false;
    public socketListenerTimeout: any;
    // @API cases/mortgage
    protected serviceName = 'cases/mortgage';
    private $mortgage: BehaviorSubject<MortgageInterface> = new BehaviorSubject(null);
    public mortgage$: Observable<MortgageInterface> = this.$mortgage.asObservable();

    constructor() {
        super();
        this.deviceService.isDesktopObservable.subscribe((isDesktop: boolean) => {
            this.isDesktop = isDesktop;
        });
        this.initConnection();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public async setMortgage(mortgage: MortgageInterface, loadFromServer: boolean = true): Promise<void> {
        this.mortgage = mortgage;

        if (this.mortgage) {
            this.recalculateFees();
            if (!this.mortgage.fillingScore) {
                this.mortgage.fillingScore = OfferStepsEnum.Calculator;
            }

            const modelations = mortgage.modelations?.map((modelation) => modelation.amount);
            if (modelations?.length) {
                mortgage.highestAmountForDisplay = Math.max(...modelations);
            }
            mortgage.highestAmountForDisplay ??= 0;

            this.mortgage.isLocked =
                this.mortgage.fillingScore >= OfferStepsEnum.Offers &&
                (this.mortgage.stage === 'create' || this.mortgage.stage === 'created');
            this.mortgage.isRefinancing = this.mortgage.purposes.some((a) => a === 'refinancing');
        }

        if (this.usersService.authenticated()) {
            this.$mortgage.next(this.mortgage);
        }
    }

    public scrollToPanel(panel: MatExpansionPanel, offset: number = -280) {
        const elem = panel._body.nativeElement;
        const y = elem ? (elem as any).getBoundingClientRect().top + window.pageYOffset + offset : 0;
        window.scrollTo({ top: y, behavior: 'smooth' });
    }

    public stopListenMessages() {
        clearTimeout(this.socketListenerTimeout);
        this.preventNavigate = true;
    }

    public startListenMessages(delay?: number) {
        clearTimeout(this.socketListenerTimeout);
        if (delay) {
            this.socketListenerTimeout = setTimeout(() => {
                this.preventNavigate = false;
            }, delay);
        } else {
            this.preventNavigate = false;
        }
    }

    public async navigateToActualStep(mortgage?: MortgageInterface, logMixpanel: boolean = false) {
        if (this.preventNavigate) {
            return;
        }

        if (mortgage) {
            await this.setMortgage(mortgage);
        } else if (this.mortgage) {
            mortgage = this.mortgage;
        } else {
            return this.goHome();
        }

        if (mortgage) {
            if (!mortgage.fillingScore) {
                mortgage.fillingScore = OfferStepsEnum.Calculator;
            }

            let mortgageSteps;
            if (this.isDesktop) {
                mortgageSteps = OfferStepsDesktop;
            } else {
                mortgageSteps = OfferStepsMobile;
            }

            const mortgageIsRefinancing = mortgage.purposes.some((a) => a === 'refinancing');
            let step;
            if (mortgageIsRefinancing) {
                step = mortgageSteps.find((x) => x.fillingScore === mortgage?.fillingScore && x.refinancing);
            }

            if (!step) {
                step = mortgageSteps.find((x) => x.fillingScore === mortgage?.fillingScore);
            }

            if (step) {
                //HP-2841
                if (
                    mortgage.fillingScore === OfferStepsEnum.DocumentsHome &&
                    !this.bridgeService.platformUserId &&
                    logMixpanel
                ) {
                    this.mixpanelService.track('$variant_selected_candidate', { mortgageId: this.mortgage._id });
                }
                return this.router.navigate([step?.url, mortgage._id]).then(() => {
                    if (mortgage.fillingScore === OfferStepsEnum.DocumentsApplication) {
                        let alreadySeen = false;
                        if (this.backendService.lsIsAvailable) {
                            alreadySeen = !!localStorage.getItem(
                                'documentApplicationConfirmDialog_' + this.mortgage._id
                            );
                        }

                        if (!alreadySeen) {
                            if (!mortgage.isPlatformChanged) {
                                this.dialogService.documentsConfirm();
                                if (this.backendService.lsIsAvailable) {
                                    localStorage.setItem(
                                        'documentApplicationConfirmDialog_' + this.mortgage._id,
                                        'true'
                                    );
                                }
                            }
                        }
                    }
                });
            } else {
                return this.goHome();
            }
        }
    }

    public async goHome() {
        await this.setMortgage(null);
        return this.router.navigate([this.isDesktop ? '/dashboard' : '/mobile/dashboard']);
    }

    public checkOrActivate(): boolean {
        const email = this.usersService.currentUserEmail();
        const isActivated = this.usersService.activated();
        const isVerified = this.usersService.emailVerified();
        const createModelations = !isActivated;
        console.log('checkOrActivate-1', {
            email,
            isActivated,
            isVerified,
            createModelations,
            user: this.usersService.user,
        }); // TODO REMOVE

        if (!isActivated || (email && !isVerified)) {
            this.dialogService
                .activate({
                    disableClose: true,
                    data: {
                        blurContent: true,
                        mortgage: this.mortgage,
                    },
                })
                .subscribe(async (mortgage: MortgageInterface) => {
                    const mortgageId = mortgage?._id;
                    if (mortgageId && createModelations) {
                        if (!this.hasVariants(mortgage)) {
                            // @API deals/auto.create
                            await this.dealsAutoService.createModelations(mortgageId);
                        }
                        const currentOffer = await this.get(mortgageId);
                        await this.navigateToActualStep(currentOffer);
                    } else {
                        await this.navigateToActualStep(mortgage);
                    }
                });
        }

        return (
            this.usersService.activated() &&
            this.usersService.currentUserEmail() !== null &&
            this.usersService.emailVerified()
        );
    }

    private recalculateFees() {
        // pokud nejsou u varianty zapsany soucty poplatku, spocitame je

        const monthlyFees: string[] = [
            'accountManagement',
            'accountStatement',
            'depositAccountManagement',
            'depositAccountStatement',
            'insurance',
        ];

        const annualFees: string[] = ['realEstateInsurance'];

        if (!this.mortgage?.modelations) {
            return;
        }

        for (const modelation of this.mortgage.modelations) {
            if (modelation.variants) {
                for (const variant of modelation.variants) {
                    if (!variant.totalFeesOnetime) {
                        let totalFeesOnetime = 0;
                        let totalFeesMonthly = 0;
                        let totalFeesAnnualy = 0;

                        if (variant.products) {
                            for (const product of variant.products) {
                                if (product.fees && product.source) {
                                    for (const key in product.fees) {
                                        const value = product.fees[key];
                                        if (monthlyFees.includes(key)) {
                                            totalFeesMonthly += value;
                                        } else if (annualFees.includes(key)) {
                                            totalFeesAnnualy += value;
                                        } else {
                                            totalFeesOnetime += value;
                                        }
                                    }
                                }
                            }
                        }

                        if (!variant.totalFeesOnetime) {
                            variant.totalFeesOnetime = totalFeesOnetime;
                        }
                        if (!variant.totalFeesMonthly) {
                            variant.totalFeesMonthly = totalFeesMonthly;
                        }
                        if (!variant.totalFeesAnnualy) {
                            variant.totalFeesAnnualy = totalFeesAnnualy;
                        }
                    }
                }
            }
        }
    }

    protected _updated(item: MortgageInterface): void {
        if (this.mortgage?._id === item?._id) {
            console.log('MortgagesService.updated', item); // TODO REMOVE
            let activate = false;
            const previous = this.usersService.currentUser();
            const contactId = previous.contactId;
            const contact = item._contacts.find((c) => c?._id === contactId);
            if (contact) {
                if (
                    contact.email !== previous.contact?.email &&
                    contact.emailVerified !== previous.contact?.emailVerified
                ) {
                    activate = true;
                    console.log('MortgagesService.updated1', item); // TODO REMOVE)
                }
                this.usersService.setCurrentUser({ ...this.usersService.currentUser(), contact });
            }

            const res = FillingScoreService.calculate({ mortgage: item });
            if (res.scoreChanged) {
                item.fillingScore = res.fillingScore;
                item.fillingScoreUpdatedAt = res.fillingScoreUpdatedAt;
            }
            if (activate) {
                console.log('MortgagesService.updated3', item); // TODO REMOVE)
                this.checkOrActivate();
            }
            if (this.preventNavigate) {
                console.log('MortgagesService.updated4', item); // TODO REMOVE)
                this.setMortgage(item);
            } else {
                console.log('MortgagesService.updated5', item); // TODO REMOVE)
                this.navigateToActualStep(item);
            }
        }
    }

    public get(id: Id, params?: Params): Promise<MortgageInterface> {
        return super
            .get(id, params)
            .then((mortgage: MortgageInterface) => {
                mortgage.fillingScore = 0;
                if (mortgage) {
                    const res = FillingScoreService.calculate({
                        mortgage: mortgage,
                    });
                    if (res.scoreChanged) {
                        mortgage.fillingScore = res.fillingScore;
                        mortgage.fillingScoreUpdatedAt = res.fillingScoreUpdatedAt;
                    }
                }
                return mortgage;
            })
            .catch((error) => {
                throw error;
            });
    }

    public patch(id: Id, data: Partial<MortgageInterface>, params?: Params): Promise<MortgageInterface> {
        return super
            .patch(id, data, params)
            .then((mortgage: MortgageInterface) => {
                mortgage.fillingScore = 0;
                if (mortgage) {
                    const res = FillingScoreService.calculate({
                        mortgage: mortgage,
                    });
                    if (res.scoreChanged) {
                        mortgage.fillingScore = res.fillingScore;
                        mortgage.fillingScoreUpdatedAt = res.fillingScoreUpdatedAt;
                    }
                }
                return mortgage;
            })
            .catch((error) => {
                throw error;
            });
    }

    public getScorePatch(mortgage: MortgageInterface): Partial<MortgageInterface> {
        if (!mortgage) {
            return {};
        }

        const res = FillingScoreService.calculate({ mortgage: mortgage });
        if (res.scoreChanged) {
            mortgage.fillingScore = res.fillingScore;
            mortgage.fillingScoreUpdatedAt = res.fillingScoreUpdatedAt;
            return { fillingScore: res.fillingScore, fillingScoreUpdatedAt: res.fillingScoreUpdatedAt };
        }

        if (mortgage.fillingScore) {
            return { fillingScore: mortgage.fillingScore, fillingScoreUpdatedAt: mortgage.fillingScoreUpdatedAt };
        }
    }

    public hasVariants(mortgage: MortgageInterface): boolean {
        return (
            mortgage?.modelations?.some((modelation) => modelation?.variants && modelation.variants.length > 0) ??
            false ??
            false
        );
    }
}
